import React, { useState, useMemo } from "react";
import { dbReview } from "./firebaseReview";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import ReviewSuccessModal from "./ReviewSuccessModal";

const categories = [
  { key: "finding", label: "Ease of Finding the Item" },
  { key: "quality", label: "Product Quality" },
  { key: "shipping", label: "Shipping Experience" },
  { key: "fit", label: "Fit & Sizing Accuracy" },
  { key: "value", label: "Value for Money" },
];

// Sanitize input
const sanitizeInput = (str) => {
  if (!str) return "";
  return str.replace(/[&<>"'`=\/]/g, (char) => {
    return {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
      "/": "&#x2F;",
      "`": "&#x60;",
      "=": "&#x3D;",
    }[char];
  });
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const CustomerReview = () => {
  const [ratings, setRatings] = useState({
    finding: 0,
    quality: 0,
    shipping: 0,
    fit: 0,
    value: 0,
  });

  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleRating = (category, value) => {
    setRatings((prev) => ({ ...prev, [category]: value }));
  };

  const averageRating = useMemo(() => {
    const values = Object.values(ratings);
    const total = values.reduce((a, b) => a + b, 0);
    return (total / categories.length).toFixed(1);
  }, [ratings]);

  const FullStar = () => (
    <svg width="28" height="28" fill="#f4b400" viewBox="0 0 24 24">
      <path d="M12 .587l3.668 7.568L24 9.748l-6 5.848L19.335 24 12 19.897 4.665 24 6 15.596 0 9.748l8.332-1.593z" />
    </svg>
  );

  const HalfStar = () => (
    <svg width="28" height="28" viewBox="0 0 24 24">
      <defs>
        <linearGradient id="halfGrad">
          <stop offset="50%" stopColor="#f4b400" />
          <stop offset="50%" stopColor="#ccc" />
        </linearGradient>
      </defs>
      <path
        fill="url(#halfGrad)"
        d="M12 .587l3.668 7.568L24 9.748l-6 5.848L19.335 24 12 19.897 4.665 24 6 15.596 0 9.748l8.332-1.593z"
      />
    </svg>
  );

  const EmptyStar = () => (
    <svg width="28" height="28" fill="#ccc" viewBox="0 0 24 24">
      <path d="M12 .587l3.668 7.568L24 9.748l-6 5.848L19.335 24 12 19.897 4.665 24 6 15.596 0 9.748l8.332-1.593z" />
    </svg>
  );

  const renderAverageStars = (avg) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (avg >= i) stars.push(<FullStar key={i} />);
      else if (avg >= i - 0.5) stars.push(<HalfStar key={i} />);
      else stars.push(<EmptyStar key={i} />);
    }
    return stars;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      alert("Please enter a valid email.");
      return;
    }

    if (name.length > 50) {
      alert("Name too long (max 50 chars)");
      return;
    }

    if (comment.length > 500) {
      alert("Comment too long (max 500 chars)");
      return;
    }

    const reviewData = {
      ratings,
      averageRating: parseFloat(averageRating),
      comment: sanitizeInput(comment),
      email: sanitizeInput(email),
      name: sanitizeInput(name),
      createdAt: serverTimestamp(),
    };

    try {
      await addDoc(collection(dbReview, "reviews"), reviewData);

      setShowSuccessModal(true);

      setRatings({ finding: 0, quality: 0, shipping: 0, fit: 0, value: 0 });
      setComment("");
      setEmail("");
      setName("");
    } catch (err) {
      console.error(err);
      alert("Something went wrong.");
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        style={{
          maxWidth: "550px",
          margin: "20px auto",
          padding: "20px",
          border: "1px solid #ddd",
          borderRadius: "12px",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <h2>Rate Your Experience</h2>

        {categories.map((cat) => (
          <div key={cat.key} style={{ marginBottom: "20px" }}>
            <p style={{ fontWeight: 600 }}>{cat.label}</p>

            <div style={{ display: "flex", gap: "8px" }}>
              {[1, 2, 3, 4, 5].map((value) => (
                <span
                  key={value}
                  onClick={() => handleRating(cat.key, value)}
                  style={{
                    fontSize: "28px",
                    cursor: "pointer",
                    color: value <= ratings[cat.key] ? "#f4b400" : "#ccc",
                  }}
                >
                  ★
                </span>
              ))}
            </div>
          </div>
        ))}

        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            background: "#fafafa",
            borderRadius: "8px",
            border: "1px solid #ddd",
            textAlign: "center",
          }}
        >
          <p style={{ fontWeight: 600 }}>Overall Rating</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "4px" }}>
            {renderAverageStars(parseFloat(averageRating))}
          </div>
          <p style={{ marginTop: "8px" }}>{averageRating} / 5</p>
        </div>

        <div style={{ marginTop: "15px" }}>
          <label style={{ fontWeight: 600 }}>Comments</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more..."
            style={{
              width: "100%",
              minHeight: "100px",
              marginTop: "6px",
              padding: "10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div style={{ marginTop: "15px" }}>
          <label style={{ fontWeight: 600 }}>Display Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={50}
            style={{
              width: "100%",
              padding: "10px",
              marginTop: "6px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div style={{ marginTop: "15px" }}>
          <label style={{ fontWeight: 600 }}>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            style={{
              width: "100%",
              padding: "10px",
              marginTop: "6px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <button
          type="submit"
          style={{
            marginTop: "20px",
            width: "100%",
            padding: "12px",
            borderRadius: "6px",
            background: "#007bff",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            fontSize: "16px",
          }}
        >
          Submit Review
        </button>
      </form>

      <ReviewSuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </>
  );
};

export default CustomerReview;
